import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { RecaptchaComponent } from 'ng-recaptcha';
import { interval, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AuthService } from 'src/app/modules/auth/shared/auth.service';
import { UserService } from 'src/app/modules/account/shared/user.service';
import { LocalStoreService } from '../../services/local-store.service';
import { WelcomeModalService } from '../../services/welcome-modal.service';
import {
  ensureRegistrationDeviceId,
  isRegistrationLimitedError,
  registrationRetryAtFromError,
  REGISTRATION_RETRY_AT_KEY,
} from '../../services/registration-limit.util';
import { environment } from 'src/environments/environment';
import {
  MIN_PASSWORD_LENGTH,
  passwordPolicyValidator,
} from '../../validators/password-policy';

// Standard registration modal. The user picks their own credentials + profile
// fields; on success the account is a clientRegistered user (same role as
// one-click) and the modal auto-logs in, closing with result 'registered' so
// the login popup underneath can close itself and reload.
//
// Mobile hardening mirrors login-popup: 90vw dialog under 768px (constructor,
// isBrowser-guarded), 16px focused inputs (iOS auto-zoom). Date of birth is
// three dropdowns (day/month/year) rather than a calendar — a calendar is
// built for picking days near today, not scrolling back decades, and the
// dropdowns raise no keyboard at all on mobile.
//
// All overlay panels (dropdowns) get baseZIndex 11000: the dialog itself
// stacks at baseZIndex 10000, and PrimeNG overlays appended to <body> would
// otherwise resolve BELOW it and render behind the modal.
@Component({
  selector: 'app-register-popup',
  templateUrl: './register-popup.component.html',
  styleUrls: ['./register-popup.component.scss'],
})
export class RegisterPopupComponent implements OnInit, OnDestroy {
  private readonly isBrowser: boolean;
  private destroy$ = new Subject<void>();
  private retryTimerSub: Subscription | null = null;
  private deviceId: string | null = null;

  @ViewChild('captchaRef') captchaRef: RecaptchaComponent;

  form: FormGroup;
  submitting = false;
  submitError: string | null = null;
  captchaToken: string | null = null;
  checkingUsername = false;

  isMobile = false;

  countries: any[] = [];
  selectedCountry: any;
  countryFlags = {
    MAD: 'https://firebasestorage.googleapis.com/v0/b/tabbassi-test.appspot.com/o/countries%2Fmorocco.png?alt=media&token=e75a5c1b-432d-477a-b4ea-1296e4dfbd81',
    TND: 'https://firebasestorage.googleapis.com/v0/b/tabbassi-test.appspot.com/o/countries%2Ftunusia.png?alt=media&token=73653d96-289a-4549-b380-5d615094a86f',
  };

  // 429 lockout state — shared localStorage keys with login-popup so one
  // limit blocks both flows.
  registrationLimited = false;
  registrationRetryAt: string | null = null;
  registrationLimitMessage = '';
  registrationLimitHelper = '';

  // Date-of-birth bounds: 18+ only (backend enforces the same floor).
  private dobMaxDate: Date;
  // Overlay panels must clear the dialog's baseZIndex (10000).
  readonly overlayBaseZIndex = 11000;
  // Full option sets…
  private dobDays: Array<{ label: string; value: number }> = [];
  private dobMonths: Array<{ label: string; value: number }> = [];
  dobYears: Array<{ label: string; value: number }> = [];
  // …and the currently-selectable subsets. On the boundary year
  // (currentYear − 18) months/days past today's month/day are removed so an
  // underage date is UNSELECTABLE, and day counts follow the chosen month
  // (no 31 February). The cross-field validator stays as backstop.
  dobMonthOptions: Array<{ label: string; value: number }> = [];
  dobDayOptions: Array<{ label: string; value: number }> = [];

  googleRecaptchaSiteKey = environment.GOOGLE_RECAPTCHA_SITE_KEY;

  constructor(
    private fb: FormBuilder,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private authService: AuthService,
    private userService: UserService,
    private translateService: TranslateService,
    private localStoreService: LocalStoreService,
    private welcomeService: WelcomeModalService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    if (this.isBrowser && window.innerWidth <= 768) {
      this.isMobile = true;
      // MUST match the global `.login-modal` mobile min-width (92vw in
      // _base.scss): a smaller inline width leaves a strip of the dialog's
      // own (theme-white) background exposed beside the card — the "white
      // bar" seen on iPhone.
      this.config.width = '92vw';
      this.config.contentStyle = {
        ...this.config.contentStyle,
        'max-width': '92vw',
      };
    }

    const now = new Date();
    this.dobMaxDate = new Date(
      now.getFullYear() - 18,
      now.getMonth(),
      now.getDate()
    );
  }

  ngOnInit(): void {
    this.buildDobOptions();
    this.initForm();
    this.loadCountries();
    this.deviceId = ensureRegistrationDeviceId(
      this.localStoreService,
      this.isBrowser
    );
    this.restoreRegistrationLimit();
    // Hold the post-registration welcome while this form is open.
    this.welcomeService.suppress();
  }

  ngOnDestroy(): void {
    this.stopRetryTimer();
    this.destroy$.next();
    this.destroy$.complete();
    this.welcomeService.release();
  }

  private initForm(): void {
    this.form = this.fb.group(
      {
        username: [
          '',
          Validators.compose([
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(32),
            Validators.pattern(/^[a-zA-Z0-9_.-]+$/),
          ]),
        ],
        // Same rules as the backend's password policy (pentest V_006).
        password: [
          '',
          Validators.compose([
            Validators.required,
            Validators.minLength(MIN_PASSWORD_LENGTH),
            Validators.maxLength(32),
            passwordPolicyValidator(() => this.form?.get('username')?.value),
          ]),
        ],
        firstname: [
          '',
          Validators.compose([Validators.required, Validators.maxLength(100)]),
        ],
        lastname: [
          '',
          Validators.compose([Validators.required, Validators.maxLength(100)]),
        ],
        email: [
          '',
          Validators.compose([Validators.email, Validators.maxLength(255)]),
        ],
        phoneNumber: [
          '',
          Validators.compose([
            Validators.required,
            Validators.pattern(/^\+?[0-9]{6,15}$/),
          ]),
        ],
        dobDay: [null, Validators.required],
        dobMonth: [null, Validators.required],
        dobYear: [null, Validators.required],
        countryId: [1, Validators.required],
      },
      { validators: this.dobValidator }
    );
    // The password must not contain the username: re-check it when the
    // username changes.
    this.form
      .get('username')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() =>
        this.form.get('password').updateValueAndValidity({ emitEvent: false })
      );
  }

  // Day/Month/Year dropdown options. Years run DESCENDING from the newest
  // eligible year (currentYear - 18) so the most common answers sit at the
  // top of the panel.
  private buildDobOptions(): void {
    this.dobDays = Array.from({ length: 31 }, (_, i) => ({
      label: String(i + 1),
      value: i + 1,
    }));
    this.dobMonths = Array.from({ length: 12 }, (_, i) => ({
      label: this.translateService.instant(`AUTH.REGISTER.MONTH_${i + 1}`),
      value: i + 1,
    }));
    const newestYear = new Date().getFullYear() - 18;
    const years: Array<{ label: string; value: number }> = [];
    for (let y = newestYear; y >= 1920; y--) {
      years.push({ label: String(y), value: y });
    }
    this.dobYears = years;
    this.dobMonthOptions = this.dobMonths;
    this.dobDayOptions = this.dobDays;
  }

  // Re-derives the selectable month/day subsets whenever year or month
  // changes, clearing any selection that just became invalid.
  onDobSelectionChange(): void {
    const year: number | null = this.form.get('dobYear').value;
    const boundaryYear = this.dobMaxDate.getFullYear();
    const boundaryMonth = this.dobMaxDate.getMonth() + 1;
    const boundaryDay = this.dobMaxDate.getDate();

    const maxMonth = year === boundaryYear ? boundaryMonth : 12;
    this.dobMonthOptions = this.dobMonths.slice(0, maxMonth);
    if (this.form.get('dobMonth').value > maxMonth) {
      this.form.patchValue({ dobMonth: null, dobDay: null });
    }

    const month: number | null = this.form.get('dobMonth').value;
    // Days in the selected month (leap-aware once the year is known).
    let maxDay = 31;
    if (month) {
      maxDay = new Date(year || 2000, month, 0).getDate();
      if (year === boundaryYear && month === boundaryMonth) {
        maxDay = Math.min(maxDay, boundaryDay);
      }
    }
    this.dobDayOptions = this.dobDays.slice(0, maxDay);
    if (this.form.get('dobDay').value > maxDay) {
      this.form.patchValue({ dobDay: null });
    }
  }

  // Cross-field validation of the three DOB selects: the combination must be
  // a real calendar date (e.g. rejects 31 Feb) and at least 18 years back.
  // Missing selections are left to each control's own `required`.
  private dobValidator = (group: FormGroup): ValidationErrors | null => {
    const day = group.get('dobDay') && group.get('dobDay').value;
    const month = group.get('dobMonth') && group.get('dobMonth').value;
    const year = group.get('dobYear') && group.get('dobYear').value;
    if (!day || !month || !year) {
      return null;
    }
    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return { dobInvalid: true };
    }
    if (date.getTime() > this.dobMaxDate.getTime()) {
      return { dobUnderage: true };
    }
    return null;
  };

  get dobTouched(): boolean {
    return ['dobDay', 'dobMonth', 'dobYear'].some((name) => {
      const control = this.form.get(name);
      return !!control && (control.dirty || control.touched);
    });
  }

  get dobMissing(): boolean {
    return ['dobDay', 'dobMonth', 'dobYear'].some((name) => {
      const control = this.form.get(name);
      return !!control && control.hasError('required');
    });
  }

  loadCountries(): void {
    this.authService
      .getCountriesPartial(1, 1000)
      .then((response) => {
        let countriesList = [];
        if (response && Array.isArray(response)) {
          countriesList = response;
        } else if (response && Array.isArray(response.data)) {
          countriesList = response.data;
        }
        if (countriesList.length === 0) {
          return;
        }

        let mapped = countriesList.map((country: any) => ({
          ...country,
          flag:
            country.countryFlag || this.countryFlags[country.currency] || null,
        }));

        // Default country (id=1) first, mirroring the one-click picker.
        const defaultCountry = mapped.find((c) => c.id === 1);
        if (defaultCountry) {
          mapped = [defaultCountry, ...mapped.filter((c) => c.id !== 1)];
          this.selectedCountry = defaultCountry;
          this.form.patchValue({ countryId: 1 });
        } else {
          this.selectedCountry = mapped[0];
          this.form.patchValue({ countryId: mapped[0].id });
        }
        this.countries = mapped;
      })
      .catch(() => {
        // Country list failure leaves the default id=1 in place — the
        // backend validates the id anyway.
      });
  }

  onCountryChange(event: any): void {
    const country = this.countries.find((c) => c.id === event.value);
    if (country) {
      this.selectedCountry = country;
    }
  }

  // Availability pre-check on blur via the same public endpoint the login
  // flow uses (success=true means the username resolves to an account).
  // Purely UX — the backend still 409s on the race.
  checkUsernameAvailability(): void {
    const control = this.form.get('username');
    if (!control || control.invalid || !control.value) {
      return;
    }
    const username = String(control.value).trim();
    this.checkingUsername = true;
    this.userService
      .getUserEmailByUsername({ username })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          this.checkingUsername = false;
          if (res && res.success) {
            control.setErrors({ ...(control.errors || {}), taken: true });
          }
        },
        () => {
          // Availability probe failure is not a validation verdict.
          this.checkingUsername = false;
        }
      );
  }

  onCaptchaResolved(token: string | null): void {
    // ng-recaptcha fires with null when the token expires.
    this.captchaToken = token || null;
  }

  private resetCaptcha(): void {
    this.captchaToken = null;
    if (this.captchaRef) {
      this.captchaRef.reset();
    }
  }

  get submitDisabled(): boolean {
    return (
      this.submitting ||
      this.registrationLimited ||
      !this.captchaToken ||
      this.form.invalid
    );
  }

  submit(): void {
    if (this.registrationLimited || this.submitting) {
      return;
    }
    if (this.form.invalid || !this.captchaToken) {
      Object.keys(this.form.controls).forEach((name) =>
        this.form.controls[`${name}`].markAsTouched()
      );
      return;
    }

    this.submitting = true;
    this.submitError = null;

    const v = this.form.value;
    const username = String(v.username).trim();
    // Compose the three selects into a UTC-midnight ISO date (timezone-proof:
    // a local-time Date could serialize as the previous day in UTC).
    const dob = new Date(Date.UTC(v.dobYear, v.dobMonth - 1, v.dobDay));
    const payload: any = {
      username,
      password: v.password,
      firstname: String(v.firstname).trim(),
      lastname: String(v.lastname).trim(),
      phoneNumber: String(v.phoneNumber).trim(),
      dateOfBirth: dob.toISOString(),
      countryId: v.countryId || 1,
    };
    const email = (v.email || '').trim();
    if (email) {
      payload.email = email;
    }
    if (this.deviceId) {
      payload.deviceId = this.deviceId;
    }

    this.authService
      .registration(payload, this.captchaToken)
      .then(() => {
        // Queue the one-time welcome modal (eligibility — TND +
        // clientRegistered — is checked by the consumer). Marked before the
        // auto-login so a failed login still greets after a manual sign-in.
        this.welcomeService.markPending();
        this.autoLogin(username, v.password);
      })
      .catch((err) => this.handleSubmitError(err));
  }

  private handleSubmitError(err: any): void {
    this.submitting = false;
    // The captcha token was consumed server-side — a retry needs a fresh one.
    this.resetCaptcha();

    const body = err && err.error;
    const code =
      (body && typeof body === 'object' && (body.errorCode || body.code)) ||
      null;

    if (code === 'USERNAME_TAKEN') {
      const control = this.form.get('username');
      control.setErrors({ ...(control.errors || {}), taken: true });
      control.markAsTouched();
      return;
    }
    if (code === 198) {
      // Backend password policy (pentest V_006) — e.g. a common password the
      // client-side copy of the list does not know yet.
      const control = this.form.get('password');
      control.setErrors({ ...(control.errors || {}), passwordPolicy: 'SERVER' });
      control.markAsTouched();
      return;
    }
    if (code === 'EMAIL_TAKEN') {
      const control = this.form.get('email');
      control.setErrors({ ...(control.errors || {}), taken: true });
      control.markAsTouched();
      return;
    }
    if (code === 'UNDERAGE') {
      // Backend re-check tripped — surface it on the DOB row (form-level,
      // same place the client-side validator reports).
      this.form.setErrors({ ...(this.form.errors || {}), dobUnderage: true });
      ['dobDay', 'dobMonth', 'dobYear'].forEach((name) =>
        this.form.get(name).markAsTouched()
      );
      return;
    }
    if (isRegistrationLimitedError(err)) {
      this.applyRegistrationLimit(err);
      return;
    }

    this.submitError =
      (body && typeof body === 'object' && body.message) ||
      this.translateService.instant('ALERT.SOMETHING_WENT_WRONG');
  }

  // Same auto-login path as one-click: username → email → loginWithMail.
  private autoLogin(username: string, password: string): void {
    this.userService
      .getUserEmailByUsername({ username })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        async (res) => {
          if (res && res.success) {
            const result = await this.authService.loginWithMail({
              email: res.data,
              password,
            });
            this.submitting = false;
            if (result && result.success) {
              this.authService.refreshUserAccount();
              this.ref.close('registered');
              return;
            }
          } else {
            this.submitting = false;
          }
          // Account exists but the automatic login failed — close back to the
          // login popup where the user can sign in manually.
          this.ref.close('registered-login-failed');
        },
        () => {
          this.submitting = false;
          this.ref.close('registered-login-failed');
        }
      );
  }

  // ---- 429 lockout (shared storage with login-popup) ----------------------

  private applyRegistrationLimit(err: any): void {
    const retryAt = registrationRetryAtFromError(err);
    this.registrationLimited = true;
    this.registrationRetryAt = retryAt;
    this.updateLimitMessages();
    this.localStoreService.setItem(REGISTRATION_RETRY_AT_KEY, retryAt);
    this.startRetryTimer();
  }

  private restoreRegistrationLimit(): void {
    const retryAt = this.localStoreService.getItem<string>(
      REGISTRATION_RETRY_AT_KEY
    );
    if (!retryAt) {
      return;
    }
    if (new Date(retryAt).getTime() <= Date.now()) {
      this.clearRegistrationLimit();
      return;
    }
    this.registrationLimited = true;
    this.registrationRetryAt = retryAt;
    this.updateLimitMessages();
    this.startRetryTimer();
  }

  private clearRegistrationLimit(): void {
    this.registrationLimited = false;
    this.registrationRetryAt = null;
    this.registrationLimitMessage = '';
    this.registrationLimitHelper = '';
    this.localStoreService.removeItem(REGISTRATION_RETRY_AT_KEY);
    this.stopRetryTimer();
  }

  private updateLimitMessages(): void {
    if (!this.registrationRetryAt) {
      this.registrationLimitMessage = '';
      this.registrationLimitHelper = '';
      return;
    }
    const retryDate = new Date(this.registrationRetryAt);
    const msLeft = retryDate.getTime() - Date.now();
    const minutes = Math.max(1, Math.ceil(msLeft / 60000));
    const hh = String(retryDate.getHours()).padStart(2, '0');
    const mm = String(retryDate.getMinutes()).padStart(2, '0');
    this.registrationLimitMessage = this.translateService.instant(
      'AUTH.REGISTER.TEMPORARILY_LIMITED',
      { time: `${hh}:${mm}`, minutes }
    );
    this.registrationLimitHelper = this.translateService.instant(
      'AUTH.REGISTER.TEMPORARILY_LIMITED_HELPER'
    );
  }

  private startRetryTimer(): void {
    if (!this.isBrowser) {
      return;
    }
    this.stopRetryTimer();
    this.retryTimerSub = interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (
          !this.registrationRetryAt ||
          new Date(this.registrationRetryAt).getTime() <= Date.now()
        ) {
          this.clearRegistrationLimit();
          return;
        }
        this.updateLimitMessages();
      });
  }

  private stopRetryTimer(): void {
    if (this.retryTimerSub) {
      this.retryTimerSub.unsubscribe();
      this.retryTimerSub = null;
    }
  }

  // ---- template helpers ----------------------------------------------------

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.form.controls[`${controlName}`];
    if (!control) {
      return false;
    }
    return (
      control.hasError(`${validationType}`) &&
      (control.dirty || control.touched)
    );
  }

  closeModal(): void {
    this.ref.close();
  }
}
